import React, { useEffect, useRef, useState } from 'react';
import block from 'propmods';
import MonacoEditor, { MonacoEditorProps } from 'react-monaco-editor';
import { editor as globalEditor, MarkerSeverity, KeyMod, KeyCode, languages } from '../../lib/monaco';
import { GrammarValidationErrorPayload } from '../../model/grammar';

import './GranetCodeEditor.scss';

const b = block('GranetCodeEditor');

interface Props {
    code: string;
    onChange: (value: string) => void;
    readOnly?: boolean;
    validationError?: GrammarValidationErrorPayload;
    onSave?: () => void;
    completionProvider?: languages.CompletionItemProvider;
}

export const GranetCodeEditor: React.FC<Props> = ({
    code,
    onChange,
    readOnly,
    validationError,
    onSave,
    completionProvider,
}) => {
    const editorRef = useRef<MonacoEditor>(null);
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (completionProvider) {
            const disposeObj = languages.registerCompletionItemProvider('granet', completionProvider);

            return () => disposeObj.dispose();
        }
    }, [completionProvider]);

    useEffect(() => {
        const editor = editorRef.current && editorRef.current.editor;

        if (editor) {
            const focusDisposable = editor.onDidFocusEditorText(() => setFocused(true));
            const blurDisposable = editor.onDidBlurEditorText(() => setFocused(false));

            return () => {
                focusDisposable.dispose();
                blurDisposable.dispose();
            };
        }
    }, []);

    useEffect(() => {
        const editor = editorRef.current && editorRef.current.editor;

        if (editor && onSave) {
            const disposable = editor.addAction({
                id: 'save',
                label: 'Сохранить',
                keybindings: [KeyMod.CtrlCmd | KeyCode.KEY_S],
                run: onSave,
            });

            return () => disposable.dispose();
        }
    }, [onSave]);

    useEffect(() => {
        const editor = editorRef.current && editorRef.current.editor;

        if (editor) {
            if (validationError && validationError.errorCode === 'VALIDATION_ERROR') {
                const { errorBounds, text } = validationError;
                const { lineNumber, charOffset, charCount } = errorBounds;

                const line = lineNumber + 1;
                const column = charOffset + 1;

                globalEditor.setModelMarkers(editor.getModel()!, '', [
                    {
                        startLineNumber: line,
                        startColumn: column,
                        endLineNumber: line,
                        endColumn: column + charCount,
                        message: text,
                        severity: MarkerSeverity.Error,
                    },
                ]);
            } else {
                globalEditor.setModelMarkers(editor.getModel()!, '', []);
            }
        }
    }, [validationError]);

    const options: MonacoEditorProps['options'] = {
        minimap: {
            enabled: false,
        },
        readOnly: Boolean(readOnly),
        scrollBeyondLastLine: false,
        lineNumbersMinChars: 3,
        scrollbar: {
            verticalScrollbarSize: 5,
            horizontalScrollbarSize: focused ? 2 : 0,
            useShadows: false,
        },
        automaticLayout: true,
        wordBasedSuggestions: false,
    };

    return (
        <div {...b()}>
            <MonacoEditor
                language="granet"
                theme="vs"
                ref={editorRef}
                height="100%"
                width="100%"
                value={code}
                options={options}
                onChange={onChange}
            />
        </div>
    );
};
